import React, { useState, useEffect } from 'react';
import {
  Button, Box, TextField,
  Typography,
  Switch, FormControlLabel, Alert, Stack, Chip,
} from '@mui/material';
import { updateGalleryImage, GalleryImage } from '../../services/gallery_api';
import { GALLERY_CAPTION_MAX_LENGTH } from '../../utils/subscription';
import GameSelectDialog, { GameSelectResult } from '../dialogs/GameSelectDialog';
import BaseDialog from '../dialogs/BaseDialog';

interface Props {
  open: boolean;
  image: GalleryImage | null;
  onClose: () => void;
  onUpdated: (image: GalleryImage) => void;
}

const GalleryEditDialog: React.FC<Props> = ({ open, image, onClose, onUpdated }) => {
  const [caption, setCaption] = useState('');
  const [game, setGame] = useState<{ game_id: number; name: string; japanese_name?: string | null } | null>(null);
  const [gameSelectOpen, setGameSelectOpen] = useState(false);
  const [isSpoiler, setIsSpoiler] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ダイアログ表示時に画像の現在値を反映
  useEffect(() => {
    if (open && image) {
      setCaption(image.caption || '');
      setGame(image.game ? { game_id: image.game.id, name: image.game.name } : null);
      setIsSpoiler(image.is_spoiler);
      setError(null);
    }
  }, [open, image]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!image) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateGalleryImage(image.id, {
        caption: caption.trim() || null,
        game_id: game?.game_id ?? null,
        is_spoiler: isSpoiler,
      });
      onUpdated(updated);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || '更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <BaseDialog
        open={open}
        onClose={handleClose}
        title="画像情報を編集"
        maxWidth="sm"
        actions={
          <>
            <Button onClick={handleClose} disabled={saving}>キャンセル</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? '保存中...' : '保存'}
            </Button>
          </>
        }
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2}>
          {image?.thumb_url && (
            <Box sx={{ width: '100%', maxHeight: 200, overflow: 'hidden', borderRadius: 1, backgroundColor: '#000' }}>
              <img
                src={image.thumb_url}
                alt=""
                style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }}
              />
            </Box>
          )}

          <TextField
            label="コメント (任意)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            multiline
            rows={2}
            inputProps={{ maxLength: GALLERY_CAPTION_MAX_LENGTH }}
            helperText={`${caption.length} / ${GALLERY_CAPTION_MAX_LENGTH}`}
            fullWidth
          />

          <Box>
            <Typography variant="caption" color="text.secondary">紐付けゲーム (任意)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {game ? (
                <Chip
                  label={game.japanese_name || game.name}
                  onDelete={() => setGame(null)}
                  sx={{ maxWidth: '100%' }}
                />
              ) : (
                <Button size="small" variant="outlined" onClick={() => setGameSelectOpen(true)}>
                  ゲームを選択
                </Button>
              )}
            </Box>
          </Box>

          <FormControlLabel
            control={<Switch checked={isSpoiler} onChange={(e) => setIsSpoiler(e.target.checked)} />}
            label="ネタバレを含む"
          />
        </Stack>
      </BaseDialog>

      <GameSelectDialog
        open={gameSelectOpen}
        onClose={() => setGameSelectOpen(false)}
        onSelect={(g: GameSelectResult) => { setGame(g); setGameSelectOpen(false); }}
        title="紐付けゲームを選択"
      />
    </>
  );
};

export default GalleryEditDialog;
